import {
  ChangeDetectionStrategy,
  ElementRef,
  Component,
  inject,
  input,
  output,
  signal,
  computed,
  booleanAttribute,
  type InputSignal,
  type OutputEmitterRef,
  type WritableSignal,
  type Signal,
  type InputSignalWithTransform
} from '@angular/core';
import { IdService } from '../../services/id.service';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { DROPDOWN_ITEM_CLICKABLE, type DropdownItemClickHandler } from './dropdown.domain';

import type { SelectableItem } from '../../domain/selectable.domain';

@Component({
  selector: 'td-wb-dropdown-item, td-wb-item',
  templateUrl: './dropdown-item.component.html',
  styleUrls: ['dropdown-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: {
    '[class.td-wb-dropdown-item--active]': 'active()',
    '[class.td-wb-dropdown-item--selected]': 'selected()',
    '[class.td-wb-dropdown-item--first]': 'first()',
    '[class.td-wb-dropdown-item--last]': 'last()',
    '[attr.id]': 'id',
    '[attr.role]': '"option"',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-selected]': 'ariaSelected()',
    '(keydown.enter)': 'onKeyDown()',
    '(keydown.space)': 'onKeyDown()',
    '(click)': 'onClick()'
  }
})

export class DropdownItemComponent<T> implements SelectableItem<T> {
  private static readonly ID_PREFIX: string = 'td-wb-dropdown-option';

  readonly value: InputSignal<T | undefined> = input<T | undefined>();

  readonly disabledSignal: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { alias: 'disabled', transform: booleanAttribute });

  get disabled(): boolean {
    return this.disabledSignal();
  }

  readonly onSelectionChange: OutputEmitterRef<SelectionChangeEvent<SelectableItem<T>>> = output<SelectionChangeEvent<SelectableItem<T>>>();

  readonly active: WritableSignal<boolean> = signal(false);
  readonly selected: WritableSignal<boolean> = signal(false);
  readonly first: WritableSignal<boolean> = signal(false);
  readonly last: WritableSignal<boolean> = signal(false);
  private readonly idService: IdService = inject(IdService);
  readonly id: string = this.idService.generateNamespaceSequentialId(DropdownItemComponent.ID_PREFIX);

  public readonly elementRef: ElementRef = inject(ElementRef);
  private readonly dropdownItemClickHandler: DropdownItemClickHandler<T> | null = inject(DROPDOWN_ITEM_CLICKABLE, { optional: true, self: true }) as DropdownItemClickHandler<T> | null;

  readonly tabIndex: Signal<string> = computed(() => this.disabled ? '-1' : '0');

  readonly ariaSelected: Signal<string | undefined> = computed(() => !this.active() ? undefined : 'true');

  onKeyDown(): boolean {
    this.select(true);
    return false;
  }

  onClick(): void {
    if (this.dropdownItemClickHandler === undefined || this.dropdownItemClickHandler === null) {
      this.select(true);
    } else {
      this.dropdownItemClickHandler.onClick(this);
    }
  }

  /**
   * Highlightable.setActiveStyles
   */
  setActiveStyles(): void {
    if (!this.active()) {
      this.active.set(true);
    }
  }

  /**
   * Highlightable.setInactiveStyles
   */
  setInactiveStyles(): void {
    if (this.active()) {
      this.active.set(false);
    }
  }

  /**
   * SelectableItem.getLabel
   */
  getLabel(): string {
    return this.elementRef.nativeElement.textContent?.trim() ?? '';
  }

  /**
   * SelectableItem.select
   */
  select(fromView: boolean = false): void {
    // disabled items can't be selected but if the model value changes the dropdown will show it
    if (this.disabled) {
      return;
    }

    // If the item is not selected or if coming from view, we need to select and close the dropdown
    if (!this.selected() || fromView) {
      this.selected.set(true);
      this.emitSelectionChangeEvent(fromView);
    }
  }

  /**
   * SelectableItem.deselect
   */
  deselect(): void {
    if (this.selected()) {
      this.selected.set(false);
      this.emitSelectionChangeEvent();
    }
  }

  /**
   * SelectableItem.getValue
   */
  getValue(): T {
    return this.value();
  }

  /**
   * SelectableItem.isSelected
   */
  isSelected(): boolean {
    return this.selected();
  }

  isActive(): boolean {
    return this.active();
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  private emitSelectionChangeEvent(fromView: boolean = false): void {
    this.onSelectionChange.emit(new SelectionChangeEvent(this, fromView));
  }

}
